'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactFlowProvider } from 'reactflow';
import * as XLSX from 'xlsx';
import { useWorkflowStore } from '../stores/workflowStore';
import { useWorkflowListStore } from '../stores/workflowListStore';
import { useUploadedFileStore } from '../stores/uploadedFileStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useToastActions } from '../components/ui/Toast';
import {
  Play,
  FloppyDisk,
  Trash,
  X,
  Check,
  Robot,
} from '@phosphor-icons/react';
import { DataArea } from '../components/workflow/DataArea';
import { AIDrawer } from '../components/workflow/AIDrawer';
import { NodePanel } from '../components/workflow/NodePanel';
import { WorkflowCanvas } from '../components/workflow/WorkflowCanvas';
import { PropertyPanel } from '../components/workflow/PropertyPanel';
import { useSchema } from '../contexts/SchemaContext';
import { workflowApi } from '../services/api';

// ── Save Dialog ──────────────────────────────────────────────────────────────
function SaveDialog({
  isOpen,
  onClose,
  onSave,
  initialName,
  initialDescription,
  isEdit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string, status: 'draft' | 'active') => void;
  initialName: string;
  initialDescription: string;
  isEdit: boolean;
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [status, setStatus] = useState<'draft' | 'active'>('active');

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
    }
  }, [isOpen, initialName, initialDescription]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <FloppyDisk className="w-5 h-5 text-emerald-600" weight="fill" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">
                  {isEdit ? '保存修改' : '保存工作流'}
                </h3>
                <p className="text-xs text-zinc-500">
                  {isEdit ? '更新工作流信息' : '为此工作流命名后保存'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <Input
              label="工作流名称"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：销售数据清洗流程"
              required
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-700">描述（可选）</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="简要描述此工作流的用途..."
                rows={3}
                className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-700">状态</label>
              <div className="flex gap-2">
                {(['active', 'draft'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all ${
                      status === s
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                    }`}
                  >
                    {s === 'active' ? '启用' : '草稿'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-zinc-50/50 border-t border-zinc-100 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>取消</Button>
            <Button
              variant="primary"
              onClick={() => onSave(name, description, status)}
              disabled={!name.trim()}
              leftIcon={<Check className="w-4 h-4" />}
            >
              {isEdit ? '保存修改' : '保存工作流'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── TopBar ───────────────────────────────────────────────────────────────────
function TopBar({
  workflowName,
  isEditMode,
  onSave,
  onRun,
  onClear,
  isRunning,
  hasFile,
  onOpenAI,
  onTitleChange,
}: {
  workflowName?: string;
  isEditMode: boolean;
  onSave: () => void;
  onRun: () => void;
  onClear: () => void;
  isRunning: boolean;
  hasFile: boolean;
  onOpenAI: () => void;
  onTitleChange?: (newName: string) => void;
}) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    if (!isEditMode || !onTitleChange) return;
    setEditValue(workflowName || '');
    setIsEditingTitle(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const finishEditing = () => {
    if (!isEditingTitle) return;
    setIsEditingTitle(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== workflowName && onTitleChange) {
      onTitleChange(trimmed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finishEditing();
    }
    if (e.key === 'Escape') {
      setEditValue(workflowName || '');
      setIsEditingTitle(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="h-12 flex-shrink-0 flex items-center gap-2 px-4 bg-white/95 backdrop-blur-xl border-b border-zinc-200/80 shadow-[0_1px_0_0_rgba(0,0,0,0.04)]">
      {/* Logo + title */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img src="/logo.svg" alt="Logo" className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          {isEditingTitle ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={finishEditing}
              onKeyDown={handleKeyDown}
              className="text-sm font-semibold text-zinc-900 leading-tight w-full max-w-[180px] px-1.5 py-0.5 -ml-1 rounded border border-emerald-300 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              placeholder="工作流名称"
            />
          ) : (
            <h1
              className={`text-sm font-semibold text-zinc-900 leading-tight truncate max-w-[180px] ${isEditMode && onTitleChange ? 'cursor-pointer hover:text-emerald-600' : ''}`}
              onDoubleClick={startEditing}
              title={isEditMode && onTitleChange ? '双击编辑名称' : undefined}
            >
              {isEditMode && workflowName ? workflowName : '新建工作流'}
            </h1>
          )}
          <p className="text-[10px] text-zinc-400 leading-none">
            {isEditMode ? '编辑模式' : '创建模式'}
          </p>
        </div>
      </div>

      {/* AI Assistant button */}
      <motion.button
        onClick={onOpenAI}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors select-none"
      >
        <Robot className="w-3.5 h-3.5" weight="fill" />
        AI 助手
      </motion.button>

      <div className="flex-1" />

      {/* Action Buttons */}
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          leftIcon={<Trash className="w-3.5 h-3.5" />}
        >
          清空
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onSave}
          leftIcon={<FloppyDisk className="w-3.5 h-3.5" />}
        >
          保存
        </Button>
        <Button
          variant="accent"
          size="sm"
          onClick={onRun}
          isLoading={isRunning}
          disabled={!hasFile}
          leftIcon={!isRunning ? <Play className="w-3.5 h-3.5" weight="fill" /> : undefined}
        >
          运行
        </Button>
      </div>
    </div>
  );
}

// ── Resize Handle ────────────────────────────────────────────────────────────
function ResizeHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <div
      className="h-1.5 flex-shrink-0 bg-zinc-100 hover:bg-emerald-50 border-y border-zinc-200 cursor-row-resize flex items-center justify-center group transition-colors duration-150 select-none"
      onMouseDown={onMouseDown}
      title="拖拽调整面板高度，双击恢复默认"
    >
      <div className="w-14 h-0.5 rounded-full bg-zinc-300 group-hover:bg-emerald-400 transition-colors duration-150" />
    </div>
  );
}

// ── Main Editor ───────────────────────────────────────────────────────────────
function WorkflowEditor() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const isEditMode = !!workflowId;

  const { nodes, edges, addNode, addEdge, clearWorkflow, loadWorkflow, selectedNode } = useWorkflowStore();
  const { getWorkflow, addWorkflow, updateWorkflow } = useWorkflowListStore();
  // Per-node file state lives here (keyed by nodeId)
  const { files, setUploadedFile: setStoreFile, clearAllFiles } = useUploadedFileStore();

  const [isRunning, setIsRunning] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);
  const [executionResults, setExecutionResults] = useState<{
    nodeId: string;
    label: string;
    data: Record<string, unknown>[];
    columns: string[];
    rowCount: number;
    metadata?: Record<string, unknown>;
    isDiffResult?: boolean;
  }[]>([]);
  const [executionLogs, setExecutionLogs] = useState<string[]>([]);
  const [currentWorkflowName, setCurrentWorkflowName] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');

  // Vertical split state
  const [topPercent, setTopPercent] = useState(58);
  const containerRef = useRef<HTMLDivElement>(null);

  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToastActions();
  const { clearAllSchemas } = useSchema();

  // Resize drag handler
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const startY = e.clientY;
      const startPercent = topPercent;
      const containerHeight = container.getBoundingClientRect().height;

      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';

      const onMove = (moveE: MouseEvent) => {
        const delta = moveE.clientY - startY;
        const deltaPercent = (delta / containerHeight) * 100;
        const next = Math.max(25, Math.min(80, startPercent + deltaPercent));
        setTopPercent(next);
      };

      const onUp = () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [topPercent]
  );

  // Double-click handle to reset ratio
  const handleResizeDblClick = () => setTopPercent(58);

  // Load existing workflow in edit mode, or clear for new workflow
  useEffect(() => {
    if (isEditMode && workflowId) {
      const saved = getWorkflow(workflowId);
      if (saved) {
        setCurrentWorkflowName(saved.name);
        setCurrentDescription(saved.description);
        const fakeWorkflow = {
          id: saved.id,
          name: saved.name,
          description: saved.description,
          status: saved.status as 'active' | 'inactive' | 'draft',
          dag: {
            nodes: Object.fromEntries(
              saved.nodes.map((n) => [
                n.id,
                { id: n.id, type: n.type, config: n.data.config, position: n.position, inputs: [] },
              ])
            ),
            edges: saved.edges.map((e) => ({ from: e.source, to: e.target })),
          },
          createdAt: saved.createdAt,
          updatedAt: saved.updatedAt,
        };
        loadWorkflow(fakeWorkflow as Parameters<typeof loadWorkflow>[0]);

        if (saved.uploadedFile) {
          // Restore the previously saved file under the legacy key; actual
          // per-node data is not persisted (the user will re-upload if needed).
          setStoreFile({ ...saved.uploadedFile, data: [] });
        }
      } else {
        toast.error('未找到工作流，将跳转到管理页面');
        navigate('/workflows');
      }
    } else {
      // 新建工作流模式：清空所有数据
      clearWorkflow();
      clearAllFiles();
      clearAllSchemas();
      setExecutionResults([]);
      setExecutionLogs([]);
      setCurrentWorkflowName('');
      setCurrentDescription('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  const INPUT_NODE_TYPES = new Set(['load_excel', 'load_csv']);

  /** Convert a file data record back to an XLSX File object for the API. */
  const buildXlsxFile = (fileData: { name: string; columns: string[]; data: Record<string, unknown>[] }): File => {
    const aoa = [fileData.columns, ...fileData.data.map((row) => fileData.columns.map((h) => row[h] ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const baseName = fileData.name.replace(/\.[^.]+$/, '');
    return new File([buf], `${baseName}.xlsx`, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  };

  const handleRun = async () => {
    if (nodes.length === 0) {
      toast.error('请至少添加一个处理节点');
      return;
    }

    // Collect all input nodes that have a file uploaded
    const inputNodes = nodes.filter((n) => INPUT_NODE_TYPES.has(n.type ?? ''));
    const nodeFileEntries: Array<{ nodeId: string; file: File }> = [];
    for (const node of inputNodes) {
      const fd = files[node.id];
      if (fd) {
        nodeFileEntries.push({ nodeId: node.id, file: buildXlsxFile(fd) });
      }
    }

    if (nodeFileEntries.length === 0) {
      toast.error('请先在「加载 Excel」或「加载 CSV」节点中上传数据文件');
      return;
    }

    // Warn if some input nodes are still missing files
    const missing = inputNodes.filter((n) => !files[n.id]);
    if (missing.length > 0) {
      toast.warning(`注意：节点「${missing.map((n) => n.data.label).join('」「')}」尚未上传文件，将跳过`);
    }

    setIsRunning(true);
    setExecutionLogs([]);

    const logs: string[] = [];
    const addLog = (msg: string) => {
      const time = new Date().toLocaleTimeString('zh-CN');
      logs.push(`[${time}]  ${msg}`);
      setExecutionLogs([...logs]);
    };

    try {
      addLog(`[INFO] 开始执行工作流...`);
      nodeFileEntries.forEach(({ nodeId }) => {
        const fd = files[nodeId]!;
        addLog(`[INFO] 数据源 [${nodeId}]: ${fd.name}（${fd.rowCount.toLocaleString()} 行）`);
      });

      const workflowData = {
        name: currentWorkflowName || `工作流_${Date.now()}`,
        description: currentDescription || '临时工作流',
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.type,
          config: { ...(n.data?.config || { type: n.type, params: {} }) } as Record<string, unknown>,
          position: n.position,
        })),
        edges: edges.map((e) => ({ from: e.source, to: e.target })),
      };

      nodes.forEach((n) => {
        addLog(`[INFO] 节点 [${n.type}] 已加载`);
      });

      let targetWorkflowId: string;
      if (isEditMode && workflowId) {
        targetWorkflowId = workflowId;
        await workflowApi.updateWorkflow(workflowId, workflowData);
      } else {
        const response = await workflowApi.createWorkflow(workflowData);
        targetWorkflowId = (response as unknown as { data: { id: string } }).data.id;
      }

      addLog(`[INFO] 工作流已提交，开始处理数据...`);

      const result = await workflowApi.runWorkflow(targetWorkflowId, nodeFileEntries);

      // ── Grouped result: one tab per group + one merged tab ──
      if (result.is_grouped && result.grouped_results && result.grouped_results.length > 0) {
        const groupItems = result.grouped_results.map((g, idx) => ({
          nodeId: `__group_${idx}__`,
          label: `分组: ${g.group_key}`,
          data: g.rows,
          columns: g.columns,
          rowCount: g.rowCount,
        }));
        // Append a "合并结果" tab showing the flat union of all groups
        groupItems.push({
          nodeId: '__merged__',
          label: '合并结果',
          data: result.rows,
          columns: result.columns,
          rowCount: result.rowCount,
        });
        setExecutionResults(groupItems);
        addLog(`[SUCCESS] 分组完成 — ${result.grouped_results.length} 个分组，共 ${result.rowCount.toLocaleString()} 行`);
      } else {
        // ── Single result (diff or regular) ──
        const isDiff = result.columns.includes('diff_type');
        setExecutionResults([{
          nodeId: '__result_0__',
          label: isDiff ? '差异对比结果' : '执行结果',
          data: result.rows,
          columns: result.columns,
          rowCount: result.rowCount,
          isDiffResult: isDiff,
        }]);
        addLog(`[SUCCESS] 执行完成 — 输出 ${result.rowCount.toLocaleString()} 行`);
      }
      toast.success(`工作流执行完成，共 ${result.rowCount} 行结果`);
    } catch (error) {
      console.error('Run workflow error:', error);
      const msg = error instanceof Error ? error.message : '未知错误';
      addLog(`[ERROR] 执行失败: ${msg}`);
      toast.error(msg);
    } finally {
      setIsRunning(false);
    }
  };

  const handleSave = () => setIsSaveDialogOpen(true);

  const handleTitleChange = async (newName: string) => {
    if (!workflowId || !newName.trim()) return;
    try {
      await workflowApi.updateWorkflow(workflowId, { name: newName.trim() });
      setCurrentWorkflowName(newName.trim());
      updateWorkflow(workflowId, { name: newName.trim(), updatedAt: new Date().toISOString() });
      toast.success('工作流名称已保存');
    } catch (error) {
      console.error('Update workflow name error:', error);
      toast.error(error instanceof Error ? error.message : '保存失败');
    }
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  const handleRunRef  = useRef(handleRun);
  const handleSaveRef = useRef(handleSave);
  handleRunRef.current  = handleRun;
  handleSaveRef.current = handleSave;

  useEffect(() => {
    const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
    const handler = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        handleSaveRef.current();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleRunRef.current();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // refs always up-to-date; no deps needed

  const handleSaveConfirm = (name: string, description: string, status: 'draft' | 'active') => {
    const now = new Date().toISOString();
    // Persist the first uploaded file's metadata for display on the workflow list
    const firstFile = Object.values(files)[0] ?? null;
    const savedFileRef = firstFile
      ? { id: firstFile.id, name: firstFile.name, rowCount: firstFile.rowCount, columns: firstFile.columns }
      : null;

    if (isEditMode && workflowId) {
      updateWorkflow(workflowId, {
        name,
        description,
        status,
        nodes,
        edges,
        nodeCount: nodes.length,
        uploadedFile: savedFileRef,
        updatedAt: now,
      });
      toast.success('工作流已保存');
    } else {
      const id = `wf_${Date.now()}`;
      addWorkflow({
        id,
        name,
        description,
        status,
        nodes,
        edges,
        nodeCount: nodes.length,
        executionCount: 0,
        uploadedFile: savedFileRef,
        createdAt: now,
        updatedAt: now,
      });
      toast.success('工作流已创建并保存');
    }

    setIsSaveDialogOpen(false);
    navigate('/workflows');
  };

  const handleClear = async () => {
    const confirmed = await confirm({
      title: '清空工作流（含已上传文件）',
      description: '确定要清空当前工作流中的所有节点和连接吗？此操作不可撤销。',
      confirmText: '确认清空',
      cancelText: '取消',
      type: 'danger',
    });
    if (confirmed) {
      clearWorkflow();
      clearAllFiles();
      clearAllSchemas();
      setExecutionResults([]);
      setExecutionLogs([]);
      toast.success('工作流已清空');
    }
  };

  const handleDownloadResult = (
    index = 0,
    exportOverride?: { columns: string[]; rows: string[][] }
  ) => {
    const result = executionResults[index];
    if (!result) return;

    // 查找工作流中的输出节点
    const exportNode = nodes.find(n => n.type === 'export_excel' || n.type === 'export_csv');
    const nodeType = exportNode?.type || 'export_excel';
    const nodeConfig = exportNode?.data?.config?.params || {};
    
    // 获取文件名（不含扩展名），默认为 'result'
    const baseFileName = nodeConfig.fileName || 'result';

    // For diff results, use the export data built from original columns + cell decisions.
    // For regular results, use the raw columns/rows from the API response.
    let aoa: unknown[][];
    if (exportOverride) {
      aoa = [exportOverride.columns, ...exportOverride.rows];
    } else {
      aoa = [
        result.columns,
        ...result.data.map((row) => result.columns.map((c) => row[c] ?? '')),
      ];
    }

    if (nodeType === 'export_csv') {
      // CSV 导出
      const separator = (nodeConfig.separator as string) || ',';
      const encoding = (nodeConfig.encoding as string) || 'utf-8';
      
      // 将数据转换为 CSV 格式
      const csvContent = aoa.map(row => 
        row.map(cell => {
          const str = String(cell ?? '');
          // 如果包含分隔符、换行符或引号，需要转义
          if (str.includes(separator) || str.includes('\n') || str.includes('"')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(separator)
      ).join('\n');

      // 处理编码
      let blob: Blob;
      if (encoding === 'utf-8') {
        blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
      } else {
        // 对于 GBK/GB2312，需要使用 TextEncoder 或其他方式
        // 这里使用简单的 UTF-8 作为后备
        blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      }
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFileName}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      // Excel 导出
      const sheetName = (nodeConfig.sheetName as string) || 'Sheet1';
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFileName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  void addNode;
  void addEdge;

  return (
    <div className="h-full flex flex-col bg-zinc-50 overflow-hidden">
      {/* ── Top Bar ── */}
      <TopBar
        workflowName={currentWorkflowName}
        isEditMode={isEditMode}
        onSave={handleSave}
        onRun={handleRun}
        onClear={handleClear}
        isRunning={isRunning}
        hasFile={Object.keys(files).length > 0}
        onOpenAI={() => setIsAIDrawerOpen(true)}
        onTitleChange={isEditMode && workflowId ? handleTitleChange : undefined}
      />

      {/* ── Main vertical split area ── */}
      <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* ── Top workspace: NodePanel + Canvas + PropertyPanel ── */}
        <div
          style={{ height: `${topPercent}%` }}
          className="flex-shrink-0 flex overflow-hidden min-h-0"
        >
          {/* Left: Node Palette — manages its own width (collapsed / expanded) */}
          <NodePanel />

          {/* Center: ReactFlow Canvas */}
          <WorkflowCanvas />

          {/* Right: Node Properties Panel — slides in when a node is selected */}
          <AnimatePresence>
            {selectedNode && (
              <motion.div
                key="property-panel"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 288, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 260, damping: 28 }}
                className="flex-shrink-0 overflow-hidden"
              >
                <PropertyPanel className="w-72 h-full" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Resize Handle ── */}
        <div onDoubleClick={handleResizeDblClick}>
          <ResizeHandle onMouseDown={handleResizeMouseDown} />
        </div>

        {/* ── Bottom Data Area ── */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <DataArea
            executionResults={executionResults}
            executionLogs={executionLogs}
            onDownloadResult={handleDownloadResult}
          />
        </div>
      </div>

      {/* AI Drawer */}
      <AIDrawer
        isOpen={isAIDrawerOpen}
        onClose={() => setIsAIDrawerOpen(false)}
        onWorkflowGenerated={() => toast.success('AI 已生成工作流，节点已添加到画布')}
      />

      {/* Save Dialog */}
      <SaveDialog
        isOpen={isSaveDialogOpen}
        onClose={() => setIsSaveDialogOpen(false)}
        onSave={handleSaveConfirm}
        initialName={currentWorkflowName}
        initialDescription={currentDescription}
        isEdit={isEditMode}
      />

      {confirmDialog}
    </div>
  );
}

export function WorkflowEditorPage() {
  return (
    <ReactFlowProvider>
      <WorkflowEditor />
    </ReactFlowProvider>
  );
}
